import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["content", "toggler"]

  connect() {
    this.boundClose = this.close.bind(this)
    this.boundHandleKeyup = this.handleKeyup.bind(this)
    this.boundHandleBlur = this.handleBlur.bind(this)
    this.boundHandleTransitionEnd = this.handleTransitionEnd.bind(this)

    document.addEventListener("turbo:before-cache", this.boundClose)
    window.addEventListener("keyup", this.boundHandleKeyup)
    this.lastNavLink?.addEventListener("blur", this.boundHandleBlur)
    
    if (!this.prefersReducedMotion()) {
      this.innerTarget?.addEventListener("transitionend", this.boundHandleTransitionEnd)
    }
  }

  disconnect() {
    document.removeEventListener("turbo:before-cache", this.boundClose)
    window.removeEventListener("keyup", this.boundHandleKeyup)
    this.lastNavLink?.removeEventListener("blur", this.boundHandleBlur)
    this.innerTarget?.removeEventListener("transitionend", this.boundHandleTransitionEnd)
  }

  prefersReducedMotion() {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches
  }

  toggle() {
    if (this.contentTarget.hidden) {
      this.open()
      return
    }

    this.close()
  }

  open() {
    this.contentTarget.hidden = false

    requestAnimationFrame(() => {
      this.contentTarget.classList.add("active")
      this.togglerTarget.classList.add("active")
      this.togglerTarget.setAttribute("aria-expanded", "true")
    })
  }

  // Make sure the menu is closed when turbo caches the page
  close() {
    if (!this.hasContentTarget || !this.hasTogglerTarget) return

    this.contentTarget.classList.remove("active")
    // Add a closing class on here to so it transitions before hiding
    this.contentTarget.classList.add("closing")
    this.togglerTarget.classList.remove("active")
    this.togglerTarget.setAttribute("aria-expanded", "false")

    // If user prefers reduced motion, hide immediately without waiting for transition
    if (this.prefersReducedMotion()) {
      this.contentTarget.classList.remove("closing")
      this.contentTarget.hidden = true
    }
  }

  handleKeyup(event) {
    if (this.hasTogglerTarget && event.key === "Escape") {
      this.togglerTarget.focus()
      this.close()
    }
  }

  handleBlur(event) {
    // This function closes the nav if the user tabs away from the last anchor element in the menu.
    const navList = this.contentTarget.querySelector(".menu")

    if (!event.relatedTarget || !navList.contains(event.relatedTarget)) {
      this.close()
    }
  }

  handlePopoverToggle(event) {
    if (event.newState !== "closed") return

    this.close()
  }

  handleTransitionEnd(event) {
    // remove the closing class and add hidden true once animation has ended
    if (event.target !== this.innerTarget || event.propertyName !== "transform") return
    if (!this.contentTarget.classList.contains("closing")) return

    this.contentTarget.classList.remove("closing")
    this.contentTarget.hidden = true
  }

  get lastNavLink() {
    if (!this.hasContentTarget) { return }
    const navLinks = this.contentTarget.querySelectorAll("a")

    return navLinks[navLinks.length - 1]
  }

  get innerTarget() {
    if (!this.hasContentTarget) { return }
    return this.contentTarget.querySelector(".inner")
  }
}
